import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Loader2, Lock, Mail, ArrowRight, AlertTriangle } from "lucide-react";
import { AuthShowcase } from "@/components/auth/AuthShowcase";
import {
  AuthCard,
  AuthField,
  AuthSplit,
  PasswordInput,
  authInputClass,
  authPrimaryButton,
} from "@/components/auth/AuthLayout";
import type { ApiResponse, PublicConfig } from "@/types";

/** The brand panel: what the CRM is for, and a looping example of a lead becoming a won deal */
function LoginAside({ config }: { config: PublicConfig | null }) {
  return (
    <>
      <div className="space-y-4 max-w-[480px]">
        <p className="text-xs font-medium tracking-[0.14em] uppercase text-[#A9B0E8]">{config?.name || "Nelgates"} Sales CRM</p>
        <h2 className="text-4xl lg:text-[40px] font-semibold tracking-tight leading-[1.12]">
          One workspace for every lead, call and deal.
        </h2>
        <p className="text-[15.5px] text-[#C3C8F0] leading-relaxed">
          Your pipeline, customer conversations, proposals and orders in one place, so the whole team knows what to do next.
        </p>
      </div>
      <AuthShowcase />
    </>
  );
}

export function LoginPage() {
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the server refuses sign-in for too many wrong passwords (HTTP 429); the message says how long to wait
  const [lockedMessage, setLockedMessage] = useState<string | null>(null);
  // Set when the password was right and the account has two-step verification: the code is asked for next
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [config, setConfig] = useState<PublicConfig | null>(null);

  const { login, verifyTwoFactor, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate("/dashboard", { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    api
      .get<ApiResponse<PublicConfig>>("/api/auth/public-config")
      .then((res) => res?.data && setConfig(res.data))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailOrUsername.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(emailOrUsername.trim(), password, rememberMe);
      if ("challenge" in result) {
        setChallenge(result.challenge);
        setCode("");
        setUseBackup(false);
        return;
      }
      navigate("/dashboard");
    } catch (err: any) {
      if (err?.status === 429) setLockedMessage(err.message || "Too many failed sign-in attempts.");
      else setError(err.message || "That email and password did not match.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    if (!code.trim()) {
      setError(useBackup ? "Enter one of your backup codes." : "Enter the 6-digit code from your authenticator app.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await verifyTwoFactor(challenge, code.trim(), trustDevice);
      navigate("/dashboard");
    } catch (err: any) {
      if (err?.status === 429) {
        setLockedMessage(err.message || "Too many failed sign-in attempts.");
      } else if (/expired/i.test(err?.message || "")) {
        // The five minutes ran out: back to the password step
        setChallenge(null);
        setPassword("");
        setError(err.message);
      } else {
        setError(err?.message || "That code is not right.");
        setCode("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const backToPassword = () => {
    setChallenge(null);
    setCode("");
    setError(null);
    setPassword("");
  };

  if (lockedMessage) {
    return (
      <AuthSplit
        floating
        aside={<LoginAside config={config} />}
        topRight={
          <Link to="/forgot-password" className="font-medium text-primary hover:underline">
            Get help
          </Link>
        }
      >
        <div className="w-full max-w-[400px] space-y-7">
          <AuthCard tone="danger" status="Account locked" title="Too many sign-in attempts" subtitle={lockedMessage}>
            <Link to="/forgot-password" className={authPrimaryButton}>
              Reset password
            </Link>
            <p className="pt-5 border-t border-border text-[13.5px] text-muted-foreground">
              Wasn’t you? Tell your admin. They can also unlock the account from Settings › Users.
            </p>
            <button type="button" onClick={() => setLockedMessage(null)} className="text-[13.5px] font-medium text-primary hover:underline self-center">
              Back to sign in
            </button>
          </AuthCard>
        </div>
      </AuthSplit>
    );
  }

  if (challenge) {
    return (
      <AuthSplit floating aside={<LoginAside config={config} />}>
        <form onSubmit={handleVerify} className="w-full max-w-[400px] space-y-7">
          <AuthCard
            title="Two-step verification"
            subtitle={useBackup ? "Enter one of the backup codes you saved when you set this up." : "Enter the 6-digit code from your authenticator app."}
          >
            {error && (
              <div role="alert" className="flex gap-2.5 rounded-lg border border-danger/40 bg-danger-soft px-3 py-2.5 text-[13px] text-danger">
                <AlertTriangle className="size-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <AuthField id="code" label={useBackup ? "Backup code" : "Authenticator code"} icon={<Lock />} tone={error ? "bad" : undefined}>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode={useBackup ? "text" : "numeric"}
                autoComplete="one-time-code"
                maxLength={useBackup ? 16 : 7}
                placeholder={useBackup ? "xxxxx-xxxxx" : "123 456"}
                autoFocus
                className={`${authInputClass} font-mono tracking-[0.18em]`}
              />
            </AuthField>
            <label className="flex items-center gap-2.5 text-[13.5px] text-muted-foreground cursor-pointer select-none">
              <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} className="size-4 accent-[#000080] cursor-pointer" />
              Trust this device for 30 days
            </label>
            <button type="submit" disabled={submitting} className={authPrimaryButton}>
              {submitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Checking…
                </>
              ) : (
                <>
                  Verify <ArrowRight className="size-4" />
                </>
              )}
            </button>
            <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-border text-[13.5px]">
              <button type="button" onClick={() => { setUseBackup((b) => !b); setCode(""); setError(null); }} className="font-medium text-primary hover:underline">
                {useBackup ? "Use my authenticator app" : "Use a backup code instead"}
              </button>
              <button type="button" onClick={backToPassword} className="text-muted-foreground hover:text-foreground hover:underline">
                Back to sign in
              </button>
            </div>
          </AuthCard>
        </form>
      </AuthSplit>
    );
  }

  return (
    <AuthSplit
      aside={<LoginAside config={config} />}
      topRight={
        <span>
          Trouble signing in?{" "}
          <Link to="/forgot-password" className="font-medium text-primary hover:underline">
            Get help
          </Link>
        </span>
      }
    >
      <form onSubmit={handleSubmit} className="w-full max-w-[400px] space-y-7">
        <div className="space-y-1.5">
          <h2 className="text-[28px] font-semibold tracking-tight leading-[1.2] text-foreground">Sign in to your account</h2>
          <p className="text-[15px] text-muted-foreground">Welcome back. Enter your work email and password.</p>
        </div>

        {error && (
          <div role="alert" className="flex gap-2.5 rounded-lg border border-danger/40 bg-danger-soft px-3 py-2.5 text-[13px] text-danger">
            <AlertTriangle className="size-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-[18px]">
          <AuthField id="email" label="Work email" icon={<Mail />} tone={error ? "bad" : undefined}>
            <input
              id="email"
              type="text"
              value={emailOrUsername}
              onChange={(e) => setEmailOrUsername(e.target.value)}
              placeholder="you@nelgates.com"
              autoComplete="username"
              autoFocus
              className={authInputClass}
            />
          </AuthField>

          <AuthField
            id="password"
            label="Password"
            icon={<Lock />}
            tone={error ? "bad" : undefined}
            labelRight={
              <Link to="/forgot-password" className="text-[13px] font-medium text-primary hover:underline">
                Forgot password?
              </Link>
            }
            right={null}
          >
            <PasswordInput id="password" value={password} onChange={setPassword} autoComplete="current-password" />
          </AuthField>

          <label className="flex items-center gap-2.5 text-[13.5px] text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="size-4 accent-[#000080] cursor-pointer"
            />
            Keep me signed in on this device
          </label>
        </div>

        <button type="submit" disabled={submitting} className={authPrimaryButton}>
          {submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" /> Signing in…
            </>
          ) : (
            <>
              Sign in <ArrowRight className="size-4" />
            </>
          )}
        </button>

        <p className="pt-5 border-t border-border text-[13.5px] text-muted-foreground text-center">
          New to the team? Use the invite email from your admin to set up your account.
        </p>
      </form>
    </AuthSplit>
  );
}
